import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';
import { getIconComponent } from '@/lib/admin/icons';
import { 
  ArrowRight, 
  FileCheck, 
  Wrench,
  Lightbulb,
  CheckCircle,
  Database,
  Globe
} from 'lucide-react';

interface StandardCategory {
  category: string;
  category_name: string;
  count: number;
  icon: string | null;
}

const features = [
  { 
    icon: Database, 
    title: 'Unified Schemas', 
    description: 'Consistent data structures across all automotive systems' 
  },
  { 
    icon: FileCheck, 
    title: 'JSON Validation', 
    description: 'Validate your data against official schemas in real-time' 
  },
  { 
    icon: Wrench, 
    title: 'Schema Builders', 
    description: 'Build custom API schemas and ADF templates with ease' 
  },
  { 
    icon: Lightbulb, 
    title: 'Community Proposals', 
    description: 'Shape the future of automotive data standards' 
  },
  { 
    icon: Globe, 
    title: 'Open Source', 
    description: 'Free forever, community-driven development' 
  },
];

export default function Index() {
  const [categories, setCategories] = useState<StandardCategory[]>([]);
  const [totalStandards, setTotalStandards] = useState(0);

  useEffect(() => {
    async function fetchData() {
      // Fetch standards
      const { data: standardsData, count } = await supabase
        .from('data_standards')
        .select('category, category_name', { count: 'exact' })
        .eq('is_active', true);
      
      // Fetch category icons
      const { data: categoryData } = await supabase
        .from('categories')
        .select('slug, icon')
        .order('display_order');

      const categoryIcons = categoryData?.reduce((acc, cat) => {
        acc[cat.slug] = cat.icon;
        return acc;
      }, {} as Record<string, string | null>) || {};

      if (standardsData) {
        setTotalStandards(count || 0);
        const grouped = standardsData.reduce((acc, item) => {
          const existing = acc.find(c => c.category === item.category);
          if (existing) {
            existing.count++;
          } else {
            acc.push({ 
              category: item.category, 
              category_name: item.category_name, 
              count: 1,
              icon: categoryIcons[item.category] || null
            });
          }
          return acc;
        }, [] as StandardCategory[]);
        setCategories(grouped);
      }
    }
    fetchData();
  }, []);

  const stats = [
    { value: String(totalStandards), label: 'Data Standards' },
    { value: String(categories.length), label: 'Categories' },
    { value: '100%', label: 'Free & Open' },
    { value: '0', label: 'API Keys Required' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-muted-foreground mb-4">
              The Open Data Standard for Automotive
            </p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6">
              Unified data structures for automotive
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10">
              Free, open-source data standards for seamless automotive integrations. Plus tools for managing ADF leads today and migrating to modern APIs when ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link to="/api-standards">
                  Browse Standards <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/docs">Read the Docs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What are Data Standards */}
      <section className="py-20 border-t border-border bg-secondary/30">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6">What are Data Standards?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Data standards define how information should be structured and formatted when exchanging data between automotive systems. They ensure that when one system sends a "customer" or "vehicle" record, every other system knows exactly what to expect.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-background p-6 rounded-lg border border-border">
                <h3 className="font-semibold mb-2">Consistency</h3>
                <p className="text-sm text-muted-foreground">Same field names, types, and validation rules across all integrations.</p>
              </div>
              <div className="bg-background p-6 rounded-lg border border-border">
                <h3 className="font-semibold mb-2">Interoperability</h3>
                <p className="text-sm text-muted-foreground">Systems can communicate without custom mapping for each integration.</p>
              </div>
              <div className="bg-background p-6 rounded-lg border border-border">
                <h3 className="font-semibold mb-2">Validation</h3>
                <p className="text-sm text-muted-foreground">Catch errors before they cause problems downstream.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Category */}
      {categories.length > 0 && (
        <section className="py-20 border-t border-border">
          <div className="container">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-extrabold mb-2">Browse by Category</h2>
                <p className="text-muted-foreground">Explore standards organized by domain</p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/api-standards">View all <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => {
                const IconComponent = getIconComponent(cat.icon);
                return (
                  <Link key={cat.category} to={`/api-standards?category=${cat.category}`}>
                    <Card className="h-full hover:border-foreground/20 cursor-pointer group">
                      <CardHeader>
                        <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center mb-3 group-hover:bg-foreground group-hover:text-background transition-colors">
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <CardTitle className="text-base group-hover:text-foreground transition-colors">{cat.category_name}</CardTitle>
                        <CardDescription>{cat.count} standard{cat.count !== 1 ? 's' : ''}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 border-t border-border">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">Everything you need</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Tools to help you implement, validate, and maintain data standards
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-medium mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-t border-border bg-foreground text-background">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold mb-2">{stat.value}</div>
                <div className="text-background/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 border-t border-border">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Ready to standardize your data?
            </h2>
            <p className="text-muted-foreground mb-8">
              Browse API standards, build custom schemas, or manage your ADF leads—all free, no signup required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link to="/api-standards">
                  Explore Standards <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/adf-standard">ADF Tools</Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                100% Free
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Open Source
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                No API keys
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
